// The picture a price book item shows first (Amir, 25 Oct; BL-109). By default its house-style drawing
// once one has passed, in the viewer's light or dark, else its primary photo, else none. A company can
// put the photo imported from its previous system first instead (pricebook.art.showFirst: 'imported');
// an item whose photo a person added here still shows its drawing first then. A dark drawing is made
// only from a passed light one, so until it passes the light one stands in. Pictures switched off for
// the company hide nothing that has passed: pricebook.art.get keeps returning the live pair then and the
// item sheet's panel keeps showing it, so the list does the same. Pure: every surface that lists items
// reads its picture through this.
import type { ArtTheme } from './pricebook-art-style.ts';

/** What a company shows first when an item has both: Flowology's drawing (the default) or its imported photo. */
export const PICTURE_FIRST = ['drawing', 'imported'] as const;
export type PictureFirst = (typeof PICTURE_FIRST)[number];

/** A passed drawing's links, as pricebook.listItems' and getItem's `art` carry them. */
export interface DrawingLinks {
  artId: string;
  url: string | null;
  thumbUrl: string | null;
}

/** What an item carries for its pictures: its passed pair and its primary photo, either absent. */
export interface ItemPictures {
  art?: { light: DrawingLinks | null; dark: DrawingLinks | null } | null;
  /** `imported`: it came over from the company's previous system, not added here by a person. */
  picture?: { fileId: string; thumbUrl: string | null; imported?: boolean } | null;
}

export type PictureShownFirst =
  | { kind: 'drawing'; id: string; theme: ArtTheme; thumbUrl: string | null; url: string | null }
  | { kind: 'photo'; id: string; thumbUrl: string | null; imported: boolean }
  | null;

/** The item's picture shown first for a viewer in `theme`, by the company's `first`. */
export function pictureShownFirst(
  item: ItemPictures,
  theme: ArtTheme,
  first: PictureFirst = 'drawing',
): PictureShownFirst {
  const photo = item.picture
    ? {
        kind: 'photo' as const,
        id: item.picture.fileId,
        thumbUrl: item.picture.thumbUrl,
        imported: item.picture.imported === true,
      }
    : null;
  if (first === 'imported' && photo?.imported) return photo;
  const own = item.art?.[theme] ?? null;
  const drawn = own ?? item.art?.light ?? null;
  if (drawn)
    return {
      kind: 'drawing',
      id: drawn.artId,
      theme: own ? theme : 'light',
      thumbUrl: drawn.thumbUrl ?? drawn.url,
      url: drawn.url,
    };
  return photo;
}
